import React from "react";
import "./styles.css";
import "./mobile.css";

/**
 * @function FormContainer
 * @param {STRING} className - Allows us to pass a unique class to each FormContainer. 
 * @param {React.Component} children - Allows us to inject other components/content.
 * @returns {React.Component}
 * @example
 * <FormContainer className="some-class">
 *  <FormRowView/>
 * </FormContainer>
 */
export default function FormContainer(props) {
  const className = props.className ?? "";

  return (
    <div className={`${className} flex-container flex-vertical form-container`}>
      {props.children}
    </div>
  );
}

/**
 * @function FormRowView
 * @param {STRING} text - String to show the label of the form row/ui input. 
 * @param {STRING} data-point - The data point that should be added to the row
 * @param {React.Component} children - Allows us to inject other components/content.
 * @returns {React.Component}
 * @example
 * <FormRowView formLabel="This is a form row label">
 *  <UISelect/>
 * </FormRowView>
 */
// use for just text labels
export function FormRowView(props) {
  const point = props["data-point"] ?props["data-point"] : props.dataPoint ?? "";
  const dataPointLabel = props["dataPoint"] ? { "data-point": props["dataPoint"] + "_Label" } : props.dataPoint ?? "";
  const dataPointInnerLabel = props["dataPoint"] ? { "data-point": props["dataPoint"] + "_Inner_Label" } : props.dataPoint ?? "";

  const className = props.className ?? "";
  const formLabelClassName = className ? { className: `form-label ${ className }-form-label` } : { className: "form-label" };
  const formInnerLabelClassName = className ? { className: `${ className }-form-inner-label` } : {};
  const label = (
    <div { ...formLabelClassName } { ...dataPointLabel }>
      <span { ...formInnerLabelClassName } { ...dataPointInnerLabel }>{ props.text }</span>
    </div>
  );

  const formLabel = props.text !== null ? label : null;

  return (
    <FormRow className={className} formLabel={formLabel} data-point={ point }>
      {props.children}
    </FormRow>
  );
}

/**
 * @function FormRow
 * @param {React.Component} formLabel - React component to be used as label for form. 
 * @param {STRING} data-point - The data point that should be added to the row
 * @param {React.Component} children - Allows us to inject other components/content.
 * @returns {React.Component}
 * @example
 * <FormRow formLabel={<div><SVGAsset id="info" /><span>form label</span></div>}>
 *  <UISelect/>
 * </FormRow>
 */
// use for things like 
 export function FormRow(props) {
  const point = props["data-point"] ?props["data-point"] : props.dataPoint ?? "";
  const className = props.className ?? "";

  return (
    <div className={`${className} flex-container flex-vertical flex-height form-row-view`} data-point={ point }>
      {props.formLabel}
      {props.children}
    </div>
  );
}

/**
 * @function HorizontalFormRow
 * @param {React.Component} children - Allows us to inject other components/content.
 * @returns {React.Component}
 * @example
 * <HorizontalFormRow>
 *  <FormRowView text="Form Label">
 *    <UITextInput />
 * </FormRowView>
 * </HorizontalFormRow>
 */
export function HorizontalFormRow(props) {
  return (
    <FormRowView className="horizontal-form-row">
      <div className="flex-container flex-horizontal align-center">
        {props.children}
      </div>
    </FormRowView>
  );
}

/**
 * @function FormButtonRow
 * @param {React.Component} children - Allows us to inject other components/content.
 * @returns {React.Component}
 * @example
 * <FormButtonRow>
 *  <SecondaryButton text="Secondary" />
 *  <PrimaryButton text="Primary" />
 * </FormButtonRow>
 */
export function FormButtonRow(props) {
  const className = props.className ?? "";

  return (
    <FormRowView>
      <div className={"flex-container flex-horizontal align-center button-container " + className}>
        {props.children}
      </div>
    </FormRowView>
  );
}